import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// ---- base64url <-> Uint8Array helpers ----
export function b64urlToBuf(b64url) {
  const pad = '='.repeat((4 - (b64url.length % 4)) % 4);
  const base64 = (b64url + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr.buffer;
}

export function bufToB64url(buf) {
  const bytes = new Uint8Array(buf);
  let str = '';
  for (let i = 0; i < bytes.byteLength; i++) str += String.fromCharCode(bytes[i]);
  return window.btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function isWebAuthnSupported() {
  return typeof window !== 'undefined'
    && !!window.PublicKeyCredential
    && typeof navigator !== 'undefined'
    && !!navigator.credentials;
}

export async function isPlatformAuthenticatorAvailable() {
  if (!isWebAuthnSupported()) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

// ---- options decoding ----
function decodeRegistrationOptions(opts) {
  return {
    ...opts,
    challenge: b64urlToBuf(opts.challenge),
    user: { ...opts.user, id: b64urlToBuf(opts.user.id) },
    excludeCredentials: (opts.excludeCredentials || []).map(c => ({ ...c, id: b64urlToBuf(c.id) })),
  };
}

function decodeAuthenticationOptions(opts) {
  return {
    ...opts,
    challenge: b64urlToBuf(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map(c => ({ ...c, id: b64urlToBuf(c.id) })),
  };
}

function encodeRegistrationCredential(cred) {
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      attestationObject: bufToB64url(cred.response.attestationObject),
      clientDataJSON: bufToB64url(cred.response.clientDataJSON),
      transports: cred.response.getTransports ? cred.response.getTransports() : [],
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  };
}

function encodeAuthenticationCredential(cred) {
  return {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      authenticatorData: bufToB64url(cred.response.authenticatorData),
      clientDataJSON: bufToB64url(cred.response.clientDataJSON),
      signature: bufToB64url(cred.response.signature),
      userHandle: cred.response.userHandle ? bufToB64url(cred.response.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
  };
}

// ---- Public API ----
export async function registerPasskey(deviceName) {
  const beginRes = await axios.post(`${API}/webauthn/register/begin`, {}, { withCredentials: true });
  const publicKey = decodeRegistrationOptions(beginRes.data);
  const credential = await navigator.credentials.create({ publicKey });
  if (!credential) throw new Error('Registrazione annullata');
  const encoded = encodeRegistrationCredential(credential);
  await axios.post(`${API}/webauthn/register/complete`, {
    credential: encoded,
    device_name: deviceName || 'Dispositivo'
  }, { withCredentials: true });
}

export async function loginWithPasskey(email) {
  const beginRes = await axios.post(`${API}/webauthn/login/begin`, { email }, { withCredentials: true });
  const publicKey = decodeAuthenticationOptions(beginRes.data);
  const credential = await navigator.credentials.get({ publicKey });
  if (!credential) throw new Error('Autenticazione annullata');
  const encoded = encodeAuthenticationCredential(credential);
  const res = await axios.post(`${API}/webauthn/login/complete`, {
    email,
    credential: encoded
  }, { withCredentials: true });
  return res.data;
}

export async function listPasskeys() {
  const res = await axios.get(`${API}/webauthn/credentials`, { withCredentials: true });
  return res.data;
}

export async function deletePasskey(passkeyId) {
  await axios.delete(`${API}/webauthn/credentials/${passkeyId}`, { withCredentials: true });
}

export async function emailHasPasskey(email) {
  if (!email) return false;
  try {
    const res = await axios.get(`${API}/webauthn/has-passkey`, { params: { email } });
    return !!res.data?.hasPasskey;
  } catch {
    return false;
  }
}
